/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DatePicker from '../../components/DatePicker'
import Button from '../../components/Button'
import VideoGrid from '../../components/interviews/VideoGrid'
import { Tooltip, Pagination } from '@jobtarget/ui-library';
import JobListOffCanvas from '../../components/jobs/JobListOffCanvas';
import { JobData, JobsService } from '../../api/JobsService';
import { useJobSearchApi } from '../../hooks/useJobSearchApi';
import SearchSection from '../../components/jobs/SearchSection';
import { InterviewJobListContent } from '../../components/jobs/JobListContent';
import { EnrichedInterviewData } from '../../hooks/useInterviewManagement';
import QuestionCanvas from '../../components/questionnaires/QuestionCanvas';
import QuestionCanvasSimple from '../../components/questionnaires/QuestionCanvasSimple';
import QuestionList from '../../components/questionnaires/QuestionList';
import { truncateName, getStateAbbreviation } from '../../utils/dataUtils';
import { getDateRangeBounds } from '../../utils/dateRange';
import { useResponseCounts } from '../../hooks/useResponseCounts';
import { usePersistedInterviewState } from '../../hooks/usePersistedInterviewState';
import Spinner from '../../components/Spinner';
import JobTooltip from '../../components/jobs/JobToolTip';
import ExpandedInterviewPageOverlay from './ExpandedInterviewPageOverlay';
import { isExpandedInterviewOverlayFeatureEnabled, isPlatformsJobBarEnabled } from '../../utils/featureFlags';
import PlatformsJobBar from '../../components/jobs/PlatformsJobBar';
import { JobQuestionnaireData } from '../../hooks/useJobQuestionnaires';
import { buildQuestionnaireStats, useJobQuestionnaireHistory } from '../../hooks/useJobQuestionnaireHistory';
import { IJobQuestionnaireWithStats } from '../../types/questionnaire';

interface InterviewsPageProps {
  isMobile: boolean;
  jobData: JobData[] | null;
  allJobs?: JobData[] | null; // Full list of jobs for initial selection
  isLoadingJobs?: boolean;
  isLoadingAllJobs?: boolean; // Loading state for all jobs
  // New interview props
  interviews: EnrichedInterviewData[];
  isLoadingInterviews: boolean;
  isLoadingMore: boolean;
  isLoadingByJobId?: boolean;
  interviewError: string | null;
  hasMoreToLoad: boolean;
  onLoadMore: () => void;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onFilterByStatus: (status: string) => void;
  onRefresh: () => void;
  initialJobId?: number | null;
  initialJobIdSource?: 'url' | 'interview-click' | null; // Source of the initialJobId
  onFetchByJobId?: (jobId: number) => void;
  onRestoreCompanyInterviews?: () => void;
  // Questionnaire data for jobs (to show which jobs have questionnaires)
  jobsWithQuestionnaires?: Set<number>;
  jobQuestionnaireMap?: Map<number, string>;
  jobQuestionnaireDataMap?: Map<number, JobQuestionnaireData>; // Full questionnaire data including questions
  isLoadingJobQuestionnaires?: boolean;
  onJobsDiscovered?: (jobs: JobData[]) => void;
  paginationMeta?: { total: number; totalPages: number } | null;
  // VI-623: company id for the submission-counts endpoint + a callback so Home
  // can render the company-wide completed total in the "Responses (X)" tab.
  companyId: string | number;
  onResponseCountChange?: (companyCompleted: number) => void;
  // VI-623: jobId currently in view (from useInterviewManagement). Used to scope
  // response counts to the viewed job even when selectedJob UI state isn't synced
  // (e.g. arriving via the Jobs-tab completed-screener count click).
  viewingJobId?: number | null;
  // VI-753: notify the hook when the DatePicker range changes so it refetches the
  // company list server-side, date-bounded to the new range (grid == badge).
  onDateRangeChange?: (range: string) => void;
}

export interface IQuestion {
  id: number;
  text: string; 
}

const InterviewsPage = ({ 
  isMobile, 
  jobData,
  allJobs,
  isLoadingJobs,
  isLoadingAllJobs,
  interviews,
  isLoadingInterviews,
  isLoadingMore,
  isLoadingByJobId,
  hasMoreToLoad,
  onLoadMore,
  interviewError,
  currentPage,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onFilterByStatus,
  onRefresh,
  initialJobId,
  initialJobIdSource,
  onFetchByJobId,
  onRestoreCompanyInterviews,
  jobsWithQuestionnaires,
  jobQuestionnaireMap,
  jobQuestionnaireDataMap,
  isLoadingJobQuestionnaires,
  onJobsDiscovered,
  paginationMeta,
  companyId,
  onResponseCountChange,
  viewingJobId,
  onDateRangeChange
}: InterviewsPageProps) => {
    // also for Expanded Interviews we can add a page here, but we receive it via props

    // save state
    const {
      selectedJob, setSelectedJob,
      selectedDateRange, setSelectedDateRange,
      displayedCards, setDisplayedCards,
      isQuestionCanvasOpen, setIsQuestionCanvasOpen,
      selectedQuestions, setSelectedQuestions,
      appliedQuestions, setAppliedQuestions,
      selectedQuestionnaireId, setSelectedQuestionnaireId,
      appliedQuestionnaireId, setAppliedQuestionnaireId
    } = usePersistedInterviewState();

    // Overlay state for expanded interview view
    const [overlayState, setOverlayState] = useState<{
      isOpen: boolean;
      selectedVideo: any;
      allVideos: any[];
    }>({
      isOpen: false,
      selectedVideo: null,
      allVideos: []
    });

    // VI-623: client-side pagination — 9 response thumbnails per page.
    // VI-623: client-side pagination — the AC caps the grid at a max of 9
    // thumbnails per page, so the page-size options run 1–9 (default 9).
    const PAGE_SIZE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    const [pageSize, setPageSize] = useState(9);
    const [clientPage, setClientPage] = useState(1);

    // VI-623 / AS-8108: authoritative completed-response totals for the tab
    // ("Responses (X)") and the "Showing N of Y responses" header, scoped to the
    // selected date range (company-wide, plus job-level when a job is selected).
    // Scope counts to the viewed job: prefer the explicit UI selection, else fall
    // back to the job the grid is actually showing (viewingJobId) so "Showing N of
    // Y" stays accurate even when selectedJob hasn't synced (Jobs-tab count click).
    const scopeJobId = selectedJob?.id ?? viewingJobId ?? null;
    const { companyCompleted, scopeCompleted } = useResponseCounts(
      companyId,
      scopeJobId,
      selectedDateRange
    );

    // Surface the company-wide completed total up to Home for the tab label.
    useEffect(() => {
      onResponseCountChange?.(companyCompleted);
    }, [companyCompleted, onResponseCountChange]);

    // VI-753: keep the interviews hook's date range in sync with the DatePicker so
    // the company list is refetched server-side, date-bounded to the selected range
    // (the same bounds the counts badge uses → grid == badge). The initial render is
    // skipped when the range already matches the hook's default ('Last 30 Days'),
    // since the hook self-loads that range — this avoids a redundant refetch. Any
    // later change (including the interview-click override to 'Last 3 Months') syncs.
    const lastSyncedRangeRef = useRef<string>('Last 30 Days');
    useEffect(() => {
      if (selectedDateRange === lastSyncedRangeRef.current) return;
      lastSyncedRangeRef.current = selectedDateRange;
      onDateRangeChange?.(selectedDateRange);
    }, [selectedDateRange, onDateRangeChange]);

    // VI-623: the Responses tab panel unmounts/remounts on every tab switch
    // (ui-library TabPanel renders null when inactive), which otherwise leaves
    // the window scrolled down (e.g. at the pagination footer). Reset to the top
    // whenever this panel mounts. A deferred pass wins over the tab-activation
    // focus() the ui-library performs right after mount.
    useEffect(() => {
      window.scrollTo(0, 0);
      const id = window.setTimeout(() => window.scrollTo(0, 0), 0);
      return () => window.clearTimeout(id);
    }, []);

    const [datePickerDefault, setDatePickerDefault] = useState('Last 6 Months');

    // Historical questionnaires attached to the selected job (VI-143 endpoint).
    // Drives the "Filter by Questions" drawer: active + previously attached.
    const {
      links: jobQuestionnaireLinks,
      questionnairesById: jobQuestionnairesById,
      isLoading: isLoadingJobQuestionnaireHistory,
    } = useJobQuestionnaireHistory(selectedJob?.id ?? null);

    // Per-interview attribution: which of the job's linked questionnaires
    // does this interview "belong to" for filtering and counting purposes?
    //
    // Attribution rule (hybrid):
    //   1. Primary — interview.questionnaireId, when it points to one of the
    //      job's linked questionnaires. This is the common case and the
    //      authoritative source.
    //   2. Fallback — best text-overlap with the linked questionnaires'
    //      question texts. Used only when interview.questionnaireId is
    //      missing or stale (VI-485 swap-mid-flow data corruption: the
    //      recruiter swaps the job's questionnaire after screener tokens
    //      were issued, the backend doesn't update existing interview docs,
    //      so the doc field lies about which questionnaire was actually
    //      answered).
    //
    // The result is a Map<applicationGuid, attributedQuestionnaireId>: each
    // interview is attributed to AT MOST ONE questionnaire. This is what
    // lets the question filter avoid the "shared template text" leak —
    // e.g. "Tell me about yourself" appearing in two questionnaires used to
    // pull interviews from both, even when the user filtered by only one.
    const interviewAttributedQid = useMemo(() => {
      const attribution = new Map<string, string>();
      if (!selectedJob) return attribution;

      const textsByQid = new Map<string, Set<string>>();
      jobQuestionnaireLinks.forEach(link => {
        const q = jobQuestionnairesById.get(link.questionnaireId);
        if (!q) return;
        const texts = new Set(
          (q.questions || [])
            .map((qn: any) => qn.text || qn.questionText || '')
            .filter((t: string) => t.length > 0)
        );
        if (texts.size > 0) textsByQid.set(link.questionnaireId, texts);
      });

      const linkedQids = new Set(jobQuestionnaireLinks.map(l => l.questionnaireId));

      interviews.forEach(interview => {
        if (interview.jobId !== selectedJob.id) return;

        const responseTexts = (interview.responses || [])
          .map((r: any) => r?.question?.text)
          .filter((t: any): t is string => typeof t === 'string' && t.length > 0);
        if (responseTexts.length === 0) return;

        const intQid = (interview as any).questionnaireId as string | undefined;
        if (intQid && linkedQids.has(intQid)) {
          attribution.set(interview.applicationGuid, intQid);
          return;
        }

        // Fallback: pick the questionnaire whose texts overlap most with
        // this interview's responses. Ties go to the most recently linked
        // (Map iteration order = link CreatedAt DESC from the API).
        let bestQid: string | null = null;
        let bestScore = 0;
        textsByQid.forEach((texts, qid) => {
          let score = 0;
          for (const t of responseTexts) if (texts.has(t)) score++;
          if (score > bestScore) {
            bestScore = score;
            bestQid = qid;
          }
        });
        if (bestQid && bestScore > 0) {
          attribution.set(interview.applicationGuid, bestQid);
        }
      });

      return attribution;
    }, [interviews, selectedJob, jobQuestionnaireLinks, jobQuestionnairesById]);

    // Per-questionnaire completed-response counts derived from the
    // attribution map above. Each interview is counted at most once.
    const responseCountByQuestionnaire = useMemo(() => {
      const counts = new Map<string, number>();
      interviewAttributedQid.forEach(qid => {
        counts.set(qid, (counts.get(qid) ?? 0) + 1);
      });
      return counts;
    }, [interviewAttributedQid]);

    const jobQuestionnaireStats: IJobQuestionnaireWithStats[] = useMemo(
      () => buildQuestionnaireStats(jobQuestionnaireLinks, jobQuestionnairesById, responseCountByQuestionnaire),
      [jobQuestionnaireLinks, jobQuestionnairesById, responseCountByQuestionnaire]
    );

    // Per VI-485, the new two-pane drawer should
    // only render when the job actually has 2+ questionnaires with completed
    // responses; otherwise the typical-case single-pane drawer is used.
    const questionnairesWithResponsesCount = useMemo(
      () => Array.from(responseCountByQuestionnaire.values()).filter(c => c > 0).length,
      [responseCountByQuestionnaire]
    );

    const previousJobIdRef = useRef<number | null>(selectedJob?.id || null);
    const lastProcessedInitialJobId = useRef<number | null>(null);

    // Handle initial job selection from URL parameter or Jobs tab click
    useEffect(() => {
      if (initialJobId !== null && initialJobId !== undefined && initialJobId !== lastProcessedInitialJobId.current) {
        // Use allJobs (all jobs) if available, otherwise fallback to jobData (dropdown jobs)
        const jobList = allJobs || jobData;
        
        if (!jobList) {
          console.log('⏳ Waiting for jobs data to load...');
          return;
        }

        const jobToSelect = jobList.find(job => job.id === initialJobId);
        if (jobToSelect) {
          console.log('🔗 Setting initial job from parent:', jobToSelect.title, 'ID:', jobToSelect.id, 'Source:', initialJobIdSource);
          setSelectedJob(jobToSelect);
          lastProcessedInitialJobId.current = initialJobId;

          // Check if we have interviews for this job in current fetched data
          const interviewsForJob = interviews.filter(interview => interview.jobId === initialJobId);
          console.log(`📊 Found ${interviewsForJob.length} interviews for job ${initialJobId} in current data`);

          // ALWAYS fetch when coming from "Completed Interviews" click (interview-click source)
          // This ensures we get the complete set of interviews for the job, not just what's in memory
          if (initialJobIdSource === 'interview-click' && onFetchByJobId) {
            console.log(`🔄 Fetching ALL interviews for job ${initialJobId} from API (source: interview-click)...`);
            setSelectedDateRange('Last 3 Months');
            setDatePickerDefault('Last 3 Months');
            onFetchByJobId(initialJobId);
          }
          // For other sources (URL), only fetch if no interviews found in current data
          else if (interviewsForJob.length === 0 && onFetchByJobId) {
            console.log(`🔄 Fetching interviews for job ${initialJobId} from API (no local data)...`);
            onFetchByJobId(initialJobId);
          }
        } else {
          console.warn('⚠️ Job with ID', initialJobId, 'not found in available jobs');
        }
      }
    }, [initialJobId, initialJobIdSource, allJobs, jobData, setSelectedJob, interviews, onFetchByJobId]);

    // Reset lastProcessedInitialJobId when initialJobId is cleared
    useEffect(() => {
      if (initialJobId === null) {
        lastProcessedInitialJobId.current = null;
      }
    }, [initialJobId]);

    useEffect(() => {
      console.log('🔄 Filters changed - resetting state:', {
        selectedJob: selectedJob?.title || 'None',
        selectedDateRange,
        appliedQuestions
      });
      
      // ✅ Only reset displayedCards for job changes, preserve for other filters
      const currentJobId = selectedJob?.id || null;
      const previousJobId = previousJobIdRef.current;
      const jobChanged = currentJobId !== previousJobId;
      
      if (jobChanged) {
        // Job changed - full reset including displayedCards
        setDisplayedCards(9);
        setSelectedQuestions(null);
        setAppliedQuestions(null);
        setSelectedQuestionnaireId(null);
        setAppliedQuestionnaireId(null);
      } else {
        // Only date/question filters changed - preserve displayedCards
        // But ensure we don't exceed available results after filtering
        // This will be handled in a separate useEffect after videoData updates
      }
      
      // Update the ref for next comparison
      previousJobIdRef.current = currentJobId;
      
    }, [selectedJob, selectedDateRange]);

    const getQuestionsFromJob = useCallback((jobId: number): IQuestion[] => {
      if (!jobId) return [];
      
      const jobInterviews = interviews.filter(interview => interview.jobId === jobId);
      
      // If there are interviews for this job, extract questions from them
      if (jobInterviews.length > 0) {
        // Find the most recent questionnaire for this job
        const questionnairesMap = new Map<string, { date: Date, questionnaire: any, interviews: any[] }>();
        
        jobInterviews.forEach(interview => {
          const questionnaireId = interview.questionnaireId;
          if (questionnaireId && interview.questionnaire) {
            if (!questionnairesMap.has(questionnaireId)) {
              questionnairesMap.set(questionnaireId, {
                date: new Date(interview.createdAt),
                questionnaire: interview.questionnaire,
                interviews: []
              });
            }
            questionnairesMap.get(questionnaireId)!.interviews.push(interview);
            
            // Update to latest date
            const currentDate = new Date(interview.createdAt);
            if (currentDate > questionnairesMap.get(questionnaireId)!.date) {
              questionnairesMap.get(questionnaireId)!.date = currentDate;
            }
          }
        });
        
        // Get the most recent questionnaire
        let mostRecent: any = null;
        questionnairesMap.forEach((value, key) => {
          if (!mostRecent || value.date > mostRecent.date) {
            mostRecent = value;
          }
        });
        
        if (mostRecent) {
          console.log(`Using most recent questionnaire for job ${jobId}:`, mostRecent.questionnaire.questionnaireName);
          
          // Get questions only from the most recent questionnaire's interviews
          const questionsMap = new Map<string, IQuestion>();
          
          mostRecent.interviews.forEach((interview: any) => {
            if (interview.responses) {
              interview.responses.forEach((response: any) => {
                if (response.question && !questionsMap.has(response.question.text)) {
                  questionsMap.set(response.question.text, response.question);
                }
              });
            }
          });
          
          return Array.from(questionsMap.values());
        }
      }
      
      // FALLBACK: If no interviews exist, use questionnaire data from jobQuestionnaireDataMap
      if (jobQuestionnaireDataMap && jobQuestionnaireDataMap.has(jobId)) {
        const questionnaireData = jobQuestionnaireDataMap.get(jobId);
        if (questionnaireData?.questions && questionnaireData.questions.length > 0) {
          console.log(`📋 Using questionnaire data (no interviews) for job ${jobId}:`, questionnaireData.questionnaireName);
          
          // Convert questionnaire questions to IQuestion format
          return questionnaireData.questions.map((q, index) => ({
            id: index + 1, // Use index as ID since questionId is a string
            text: q.questionText
          }));
        }
      }
      
      return [];
    }, [interviews, jobQuestionnaireDataMap]);

    const getQuestionnaireTitle = useCallback((jobId: number): string | null => {
      if (!jobId) return null;
      
      const jobInterviews = interviews.filter(interview => interview.jobId === jobId);
      
      // If there are interviews, get title from interview data
      if (jobInterviews.length > 0) {
        // Find the most recent questionnaire for this job
        const questionnairesMap = new Map<string, { date: Date, questionnaire: any }>();
        
        jobInterviews.forEach(interview => {
          const questionnaireId = interview.questionnaireId;
          if (questionnaireId && interview.questionnaire) {
            if (!questionnairesMap.has(questionnaireId)) {
              questionnairesMap.set(questionnaireId, {
                date: new Date(interview.createdAt),
                questionnaire: interview.questionnaire
              });
            }
            const currentDate = new Date(interview.createdAt);
            if (currentDate > questionnairesMap.get(questionnaireId)!.date) {
              questionnairesMap.get(questionnaireId)!.date = currentDate;
            }
          }
        });
        
        // Get the most recent questionnaire
        let mostRecent: any = null;
        questionnairesMap.forEach((value, key) => {
          if (!mostRecent || value.date > mostRecent.date) {
            mostRecent = value;
          }
        });
        
        if (mostRecent?.questionnaire?.questionnaireName) {
          return mostRecent.questionnaire.questionnaireName;
        }
      }
      
      // FALLBACK: If no interviews exist, use questionnaire data from jobQuestionnaireDataMap
      if (jobQuestionnaireDataMap && jobQuestionnaireDataMap.has(jobId)) {
        const questionnaireData = jobQuestionnaireDataMap.get(jobId);
        if (questionnaireData?.questionnaireName) {
          return questionnaireData.questionnaireName;
        }
      }
      
      return null;
    }, [interviews, jobQuestionnaireDataMap]);


    // Helper function to filter by date range
    const filterByDateRange = (interviews: EnrichedInterviewData[], dateRange: string) => {
      // 'All' is unbounded on the client — show everything. (The counts endpoint
      // still needs concrete bounds; getDateRangeBounds handles that separately.)
      if (dateRange === 'All') {
        return interviews;
      }

      // VI-623: shared resolver so the grid filter and the submission-counts
      // request use identical bounds.
      const { startDate, endDate } = getDateRangeBounds(dateRange);

      return interviews.filter(interview => {
        const interviewDate = new Date(interview.createdAt);
        return interviewDate >= startDate && interviewDate <= endDate;
      });
    };

    // Update the question filtering logic in videoData useMemo
    const videoData = useMemo(() => {
      // Don't show stale data while fetching fresh interviews for a specific job
      // This prevents showing partial/incorrect counts before the fetch completes
      if (isLoadingByJobId && selectedJob) {
        return [];
      }

      let filteredInterviews: EnrichedInterviewData[] = [];

      // Questionnaire filter (VI-159/VI-485) scopes the dashboard to one of
      // the job's historical questionnaires. Attribution uses the shared
      // interviewAttributedQid map (prefers interview.questionnaireId, falls
      // back to text-overlap) so each interview belongs to AT MOST ONE
      // questionnaire — this prevents shared template texts (e.g. "Tell me
      // about yourself") from leaking interviews across questionnaires when
      // the user filters by a specific question.
      if (appliedQuestionnaireId !== null && selectedJob) {
        filteredInterviews = interviews.filter(interview => {
          if (interview.jobId !== selectedJob.id) return false;
          return (
            interviewAttributedQid.get(interview.applicationGuid) === appliedQuestionnaireId
          );
        });

        if (appliedQuestions !== null) {
          const appliedQuestionnaire = jobQuestionnaireStats.find(
            stat => stat.questionnaireId === appliedQuestionnaireId
          );
          const selectedQuestionObj = appliedQuestionnaire?.questions.find(
            q => q.id === appliedQuestions
          );
          if (selectedQuestionObj) {
            filteredInterviews = filteredInterviews.filter(interview =>
              interview.responses?.some(
                response =>
                  response.question && response.question.text === selectedQuestionObj.text
              )
            );
            console.log(
              `Questionnaire+question filter: ${filteredInterviews.length} interviews for questionnaire ${appliedQuestionnaireId} question "${selectedQuestionObj.text}"`
            );
          } else {
            console.warn('Applied question not found in selected questionnaire, showing no results');
            filteredInterviews = [];
          }
        } else {
          console.log(
            `Questionnaire filter: ${filteredInterviews.length} interviews for questionnaire ${appliedQuestionnaireId}`
          );
        }
      } else {
        // No questionnaire filter applied — show all interviews for the job.
        filteredInterviews = selectedJob
          ? interviews.filter(interview => interview.jobId === selectedJob.id)
          : interviews;
      }

      // VI-623 / VI-773: the grid shows "responses" — completed submissions.
      // Deleted responses STAY visible: retention (or a jobseeker request) removes the
      // recorded media, not the submission itself, so the card must keep its place and
      // render DeletedMediaPlaceholder with the reason (VI-491/VI-468) rather than
      // silently disappearing. VI-623's `is_deleted !== true` exclusion is intentionally
      // dropped here — QA rejected it on VI-773 (#12: "should display the reason, not delete").
      filteredInterviews = filteredInterviews.filter(
        interview => interview.status === 'Completed'
      );

      // VI-753: the company list is now server-side date-bounded (identical bounds
      // to the counts badge → grid == badge), so no client-side date filter is
      // needed for the company view. The job view still needs one because the job
      // endpoint (/api/v1/job/:jobId/interviews) isn't date-bounded server-side —
      // so filter by date client-side only when a job is selected.
      if (selectedJob) {
        filteredInterviews = filterByDateRange(filteredInterviews, selectedDateRange);
      }

      const sampledInterviews = filteredInterviews;

        const transformedData = sampledInterviews.map((interview, index) => {
          let applicantName = '';

          if (interview.firstName && interview.lastName) {
            applicantName = `${interview.firstName} ${interview.lastName}`;
          } else if (interview.firstName) {
            applicantName = `${interview.firstName} #${index + 1}`;
          } else {
            applicantName = `Applicant #${index + 1}`;
          }

          // Dynamic thumbnail logic: when a specific question is applied via
          // the VI-159 drawer, prefer that question's response thumbnail.
          let thumbnailUrl = interview.mainThumbnail;

          if (appliedQuestionnaireId !== null && appliedQuestions !== null) {
            const appliedQuestionnaire = jobQuestionnaireStats.find(
              stat => stat.questionnaireId === appliedQuestionnaireId
            );
            const selectedQuestionObj = appliedQuestionnaire?.questions.find(
              q => q.id === appliedQuestions
            );

            if (selectedQuestionObj && interview.responses) {
              const specificResponse = interview.responses.find(response =>
                response.question && response.question.text === selectedQuestionObj.text
              );
              if (specificResponse?.answer?.thumbnail && interview.contentType !== 'audio') {
                thumbnailUrl = specificResponse.answer.thumbnail;
              }
            }
          }

          return {
            id: interview.applicationGuid,
            applicantName: applicantName,
            jobTitle: interview?.jobTitle || `Job #${interview.jobId}`,
            dateApplied: new Date(interview.createdAt).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            }),
            thumbnailUrl: thumbnailUrl, // Use the dynamic thumbnail
            status: interview.status,
            contentType: interview.contentType,
            // VI-468: surface VI-491 deletion metadata so VideoGrid /
            // VideoCard / the overlay can render the placeholder UI
            // without each consumer reaching back into `interview`.
            isDeleted: interview.is_deleted === true,
            deleteReason: interview.deleteReason ?? null,
            interview: interview
          };
        });

      return transformedData;
    }, [interviews, selectedJob, selectedDateRange, appliedQuestions, appliedQuestionnaireId, jobQuestionnaireStats, getQuestionsFromJob, isLoadingByJobId, interviewAttributedQid]);

    // ✅ Add new useEffect to adjust displayedCards after filtering
    useEffect(() => {
      if (videoData.length < 9) {
        // If we have fewer than 9, show all available
        setDisplayedCards(videoData.length);
      } else {
        // If we have 9+, ensure displayedCards is at least 9
        // (but don't reduce it if user has already loaded more)
        setDisplayedCards(Math.max(displayedCards, 9));
      }
    }, [videoData.length]);

    // Mirrors the videoData filter pipeline so "Showing N submissions" agrees
    // with the rendered grid. Resolves the question via jobQuestionnaireStats
    // first (so questions from Previously Attached questionnaires resolve
    // correctly), constrains by interviewAttributedQid when a questionnaire
    // context is active (so questions sharing template text across
    // questionnaires don't cross-pollinate counts), applies the same
    // date-range filter as videoData, and counts one interview per match —
    // not one per response.
    const getQuestionResponseCount = useCallback((questionId: number): number => {
      if (questionId == null || typeof questionId !== 'number') return 0;
      if (!selectedJob) return 0;

      const activeFilterQid = appliedQuestionnaireId ?? selectedQuestionnaireId;
      const filteredQuestionnaire = activeFilterQid
        ? jobQuestionnaireStats.find(stat => stat.questionnaireId === activeFilterQid)
        : null;

      const selectedQuestionObj =
        filteredQuestionnaire?.questions.find(q => q.id === questionId) ??
        getQuestionsFromJob(selectedJob.id).find(q => q.id === questionId);

      if (!selectedQuestionObj) return 0;

      let jobInterviews = interviews.filter(interview => interview.jobId === selectedJob.id);

      // Restrict to interviews attributed to the questionnaire being
      // browsed/filtered. Without this, a question whose text also exists
      // on a different questionnaire would inflate the count with
      // interviews that belong to that other questionnaire.
      if (activeFilterQid) {
        jobInterviews = jobInterviews.filter(
          interview => interviewAttributedQid.get(interview.applicationGuid) === activeFilterQid
        );
      }

      const dateFilteredInterviews = filterByDateRange(jobInterviews, selectedDateRange);

      return dateFilteredInterviews.reduce((count, interview) => {
        const matches = interview.responses?.some(
          response => response.question?.text === selectedQuestionObj.text
        );
        return matches ? count + 1 : count;
      }, 0);
    }, [
      interviews,
      selectedJob,
      appliedQuestionnaireId,
      selectedQuestionnaireId,
      jobQuestionnaireStats,
      selectedDateRange,
      getQuestionsFromJob,
      interviewAttributedQid,
    ]);

    // VI-753: the background auto-load effect that lived here is GONE — it was the
    // VI-749 root cause (it could request page 2 before page 1 arrived, skipping the
    // newest page). The hook now fetches the full date-bounded set to completion
    // (bounded by the authoritative server total), so there is nothing to auto-load.

    // Derived pagination values over the completed, date-filtered set.
    const totalResponsePages = Math.max(1, Math.ceil(videoData.length / pageSize));
    const pageStartIndex = (clientPage - 1) * pageSize;
    const shownOnPage = Math.min(pageSize, Math.max(0, videoData.length - pageStartIndex));
    // 1-based range for "Showing A–B of N" (0 when the set is empty).
    const rangeStart = videoData.length === 0 ? 0 : pageStartIndex + 1;
    const rangeEnd = Math.min(pageStartIndex + pageSize, videoData.length);

    // Keep the current page valid as the filtered set changes.
    useEffect(() => {
      if (clientPage > totalResponsePages) {
        setClientPage(totalResponsePages);
      }
    }, [totalResponsePages, clientPage]);

    // Reset to the first page whenever the filter scope or page size changes.
    useEffect(() => {
      setClientPage(1);
    }, [selectedJob, selectedDateRange, appliedQuestions, appliedQuestionnaireId, pageSize]);

    // MEMOIZED FUNCTIONS

    const handleJobSelection = useCallback((job: JobData | null) => {
      setSelectedJob(job);
      
      // 🔥 FIX: When a job is selected, fetch interviews for that specific job from API
      // When job is cleared (null), restore company-wide interviews
      if (job && onFetchByJobId) {
        console.log(`🎯 Job selected manually: ${job.title} (ID: ${job.id}) - fetching interviews...`);
        onFetchByJobId(job.id);
      } else if (!job && onRestoreCompanyInterviews) {
        console.log('🔄 Job cleared - restoring company interviews...');
        onRestoreCompanyInterviews();
      }
    }, [onFetchByJobId, onRestoreCompanyInterviews]);

    const handleDateRangeChange = useCallback((value: string) => {
      console.log('DatePicker value:', value);
      setSelectedDateRange(value);
    }, []);

    const handleQuestionCanvasToggle = useCallback((open: boolean) => {
      // Per VI-485 (directive 2): in the multi-questionnaire
      // case, pre-seed selectedQuestionnaireId on open so the user lands on
      // the active questionnaire (or whichever one is currently applied)
      // without having to pick. Skip preselect if the user already has a
      // selection in progress.
      if (open && questionnairesWithResponsesCount >= 2 && selectedQuestionnaireId === null) {
        const next =
          jobQuestionnaireStats.find(s => s.questionnaireId === appliedQuestionnaireId) ??
          jobQuestionnaireStats.find(s => s.active) ??
          null;
        if (next) {
          setSelectedQuestionnaireId(next.questionnaireId);
        }
      }
      setIsQuestionCanvasOpen(open);
    }, [
      questionnairesWithResponsesCount,
      jobQuestionnaireStats,
      appliedQuestionnaireId,
      selectedQuestionnaireId,
      setSelectedQuestionnaireId,
      setIsQuestionCanvasOpen,
    ]);

    const handleQuestionSelection = useCallback((questions: number | null) => {
      setSelectedQuestions(questions);
    }, []);

    // When a VI-159 questionnaire filter is applied, use that questionnaire's
    // questions (so the "Filtered by" chip can resolve a question that came
    // from a previously-attached questionnaire). Otherwise fall back to the
    // most-recent-questionnaire inference from the job's interviews.
    const availableQuestions = useMemo(() => {
      const activeFilterQid = appliedQuestionnaireId ?? selectedQuestionnaireId;
      if (activeFilterQid) {
        const stat = jobQuestionnaireStats.find(s => s.questionnaireId === activeFilterQid);
        if (stat) return stat.questions;
      }
      return selectedJob ? getQuestionsFromJob(selectedJob.id) : [];
    }, [appliedQuestionnaireId, selectedQuestionnaireId, jobQuestionnaireStats, selectedJob, getQuestionsFromJob]);

    const questionnaireTitle = useMemo(() => 
      selectedJob ? getQuestionnaireTitle(selectedJob.id) : null,
      [selectedJob, getQuestionnaireTitle]
    );
    
    // Add this debug log
    console.log('🔍 CURRENT FILTER STATE:', {
      selectedJob: selectedJob?.title,
      appliedQuestions,
      selectedQuestions,
      availableQuestions: availableQuestions.length
    });

    // Handler to open the overlay
    const handleVideoCardClick = useCallback((video: any, allVideos: any[]) => {
      if (isExpandedInterviewOverlayFeatureEnabled()) {
        setOverlayState({
          isOpen: true,
          selectedVideo: video,
          allVideos: allVideos
        });
      }
      // If feature flag is off, navigation will be handled in VideoGrid
    }, []);

    // Handler to close the overlay
    const handleCloseOverlay = useCallback(() => {
      setOverlayState({
        isOpen: false,
        selectedVideo: null,
        allVideos: []
      });
    }, []);

    const ScrollToTopButton: React.FC<{ isMobile: boolean; displayedCards: number }> = ({ isMobile, displayedCards }) => {
      const [isVisible, setIsVisible] = useState(false);
    
      useEffect(() => {
        const toggleVisibility = () => {
          // Show button when user has scrolled down 300px
          if (window.pageYOffset > 300) {
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        };
    
        window.addEventListener('scroll', toggleVisibility);
    
        return () => window.removeEventListener('scroll', toggleVisibility);
      }, []);
    
      const scrollToTop = () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      };
    
      if (!isVisible) {
        return null;
      }
    
      // Different styles for mobile vs desktop
      const mobileStyles = {
        position: 'fixed' as const,
        bottom: '20px',
        right: '20px',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
      };
    
      const desktopStyles = {
        position: 'fixed' as const,
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'auto',
        height: '40px',
        borderRadius: '20px',
        padding: '0 16px',
      };
    
      return (
        <button
          onClick={scrollToTop}
          style={{
            ...(isMobile ? mobileStyles : desktopStyles),
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
            cursor: 'pointer',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMobile ? '18px' : '14px',
            fontWeight: '400',
            transition: 'all 0.3s ease',
            opacity: isVisible ? 1 : 0,
            transform: isVisible 
              ? (isMobile ? 'translateY(0)' : 'translateX(-50%) translateY(0)') 
              : (isMobile ? 'translateY(10px)' : 'translateX(-50%) translateY(-10px)'),
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#0056b3';
            if (isMobile) {
              e.currentTarget.style.transform = 'translateY(-2px)';
            } else {
              e.currentTarget.style.transform = 'translateX(-50%) translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#007bff';
            if (isMobile) {
              e.currentTarget.style.transform = 'translateY(0)';
            } else {
              e.currentTarget.style.transform = 'translateX(-50%) translateY(0)';
            }
          }}
        >
          <i className="far fa-arrow-up-to-line"></i>
          {!isMobile && <span>Back to Top</span>}
        </button>
      );
    };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        // backgroundColor: 'red',
        marginTop: '2rem',
        marginBottom: '2rem'
      }}
    >
      {/* Per VI-485 (directive 1): two-pane drawer only when 2+
          questionnaires have completed responses; otherwise fall back to the
          UAT/Production single-pane drawer. */}
      {questionnairesWithResponsesCount >= 2 ? (
        <QuestionCanvas
          isOpen={isQuestionCanvasOpen}
          onClose={() => setIsQuestionCanvasOpen(false)}
          jobHeaderContent={
            <SelectJobFilterContainer
              isMobile={isMobile}
              selectedJob={selectedJob}
              showSelectedJob={false}
              showDropdown={false}
            />
          }
          questionnaires={jobQuestionnaireStats}
          isLoadingQuestionnaires={isLoadingJobQuestionnaireHistory}
          selectedQuestionnaireId={selectedQuestionnaireId}
          onSelectQuestionnaire={setSelectedQuestionnaireId}
          selectedQuestionId={selectedQuestions}
          onSelectQuestion={setSelectedQuestions}
          onClearFilter={() => {
            setSelectedQuestionnaireId(null);
            setAppliedQuestionnaireId(null);
            setSelectedQuestions(null);
            setAppliedQuestions(null);
          }}
          onApply={() => {
            setAppliedQuestionnaireId(selectedQuestionnaireId);
            setAppliedQuestions(selectedQuestions);
            setIsQuestionCanvasOpen(false);
          }}
        />
      ) : (
        <QuestionCanvasSimple
          isOpen={isQuestionCanvasOpen}
          onClose={() => setIsQuestionCanvasOpen(false)}
          hasSelectedQuestions={selectedQuestions !== null}
          onClearFilter={() => {
            setSelectedQuestions(null);
            setAppliedQuestions(null);
            setSelectedQuestionnaireId(null);
            setAppliedQuestionnaireId(null);
          }}
        >
          <div>
            <div
              style={{
                backgroundColor: '#f2f3f7',
                width: 'calc(100% + ' + (isMobile ? '1.5rem' : '2rem') + ')',
                height: isMobile ? 'auto' : '118px',
                marginLeft: isMobile ? '-0.75rem' : '-1rem',
                marginTop: isMobile ? '-0.75rem' : '-1rem',
                marginBottom: isMobile ? '-0.75rem' : '-1rem',
                padding: isMobile ? '0.75rem' : '1rem',
                boxSizing: 'border-box',
              }}
            >
              <SelectJobFilterContainer
                isMobile={isMobile}
                selectedJob={selectedJob}
                showSelectedJob={false}
                showDropdown={false}
              />
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              {availableQuestions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  No questions available for this job
                </div>
              ) : (
                <>
                  {questionnaireTitle && (
                    <p
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 500,
                        color: 'black',
                        padding: '0.75rem',
                        marginBottom: '0.3rem',
                      }}
                    >
                      {questionnaireTitle}
                    </p>
                  )}
                  <QuestionList
                    questions={availableQuestions}
                    selectedQuestion={selectedQuestions}
                    setSelectedQuestion={setSelectedQuestions}
                    isMobile={isMobile}
                    getQuestionResponseCount={getQuestionResponseCount}
                    onQuestionSelect={(questionId) => {
                      // Auto-apply path (matches UAT/Production behavior).
                      // Also pin appliedQuestionnaireId to the only-with-
                      // responses questionnaire so videoData's filter scopes
                      // correctly even when the active questionnaire isn't
                      // the one carrying the responses.
                      const onlyWithResponses = jobQuestionnaireStats.find(
                        s => (responseCountByQuestionnaire.get(s.questionnaireId) ?? 0) > 0
                      );
                      setSelectedQuestions(questionId);
                      setAppliedQuestions(questionId);
                      if (onlyWithResponses) {
                        setSelectedQuestionnaireId(onlyWithResponses.questionnaireId);
                        setAppliedQuestionnaireId(onlyWithResponses.questionnaireId);
                      }
                      setIsQuestionCanvasOpen(false);
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </QuestionCanvasSimple>
      )}

      {/* Job selection filter */}
      <JobSelectionFilter
        isMobile={isMobile} 
        jobData={allJobs || jobData} 
        isLoadingJobs={isLoadingJobs}
        isLoadingAllJobs={isLoadingAllJobs}
        selectedJob={selectedJob}
        setSelectedJob={handleJobSelection}
        filteredCount={videoData.length}
        shownCount={shownOnPage}
        totalResponses={scopeCompleted}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={handleDateRangeChange}
        datePickerDefault={datePickerDefault}
        isQuestionCanvasOpen={isQuestionCanvasOpen}
        setIsQuestionCanvasOpen={handleQuestionCanvasToggle}
        appliedQuestions={appliedQuestions}
        selectedQuestions={selectedQuestions}
        setSelectedQuestions={handleQuestionSelection}
        setAppliedQuestions={setAppliedQuestions}
        availableQuestions={availableQuestions}
        getQuestionResponseCount={getQuestionResponseCount}
        displayedCards={displayedCards}
        questionnaireTitle={questionnaireTitle}
        onRestoreCompanyInterviews={onRestoreCompanyInterviews}
        jobsWithQuestionnaires={jobsWithQuestionnaires}
        isLoadingJobQuestionnaires={isLoadingJobQuestionnaires}
        onJobsDiscovered={onJobsDiscovered}
        paginationMeta={paginationMeta}
        isFilterDataLoading={
          !!selectedJob &&
          (isLoadingByJobId || isLoadingInterviews || isLoadingJobQuestionnaireHistory)
        }
      />

      <DisplayFilterSection
        appliedQuestion={appliedQuestions}
        isMobile={isMobile}
        availableQuestions={availableQuestions}
        setAppliedQuestion={setAppliedQuestions}
        setSelectedQuestions={setSelectedQuestions}
        setAppliedQuestionnaireId={setAppliedQuestionnaireId}
        setSelectedQuestionnaireId={setSelectedQuestionnaireId}
      />

      {/* Loading by Job ID Banner */}
      {/* {isLoadingByJobId && selectedJob && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          padding: '1rem 1.5rem',
          marginTop: '1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div>
            <strong>Loading interviews for {selectedJob.title}...</strong>
            <div style={{ fontSize: '0.9rem', color: '#856404', marginTop: '0.25rem' }}>
              Fetching all completed interviews for this job
            </div>
          </div>
        </div>
      )} */}

      {/* Video Grid */}
      <div style={{ marginTop: '2rem' }}>
        <VideoGrid
          videos={videoData}
          maxCards={pageSize}
          startIndex={pageStartIndex}
          isMobile={isMobile}
          isLoading={isLoadingInterviews}
          selectedJob={selectedJob}
          selectedDateRange={selectedDateRange}
          appliedQuestions={appliedQuestions}
          allInterviews={interviews}
          currentPage={currentPage}
          hasLoadedAll={!hasMoreToLoad}
          totalInterviews={interviews.length}
          onVideoCardClick={handleVideoCardClick}
        />
      </div>

      {/* Empty state when no results after filtering */}
      {!isLoadingInterviews && !isLoadingMore && videoData.length === 0 && interviews.length >= 0 && (
        <div style={{
          textAlign: 'center',
          marginTop: '2rem',
          padding: '2rem',
          color: '#6c757d',
          fontSize: '1rem'
        }}>
          No results for the selected time range.
        </div>
      )}

      {/* VI-623: pagination footer — numbered pages on the left, page-size
          selector + "Showing A–B of N" range on the right. Replaces "View More".
          VI-753: also gated on !isLoadingMore so the paginator/range only render
          once the full date-bounded set is loaded — never a mid-fill partial.
          videoData.length is then the complete completed-in-range count, which
          equals the "Responses (N)" badge (scopeCompleted) in the company view. */}
      {videoData.length > 0 && !isLoadingInterviews && !isLoadingMore && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem',
          marginTop: '2rem',
          marginBottom: '2rem'
        }}>
          {/* Left: numbered pagination (hidden when everything fits one page) */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {totalResponsePages > 1 && (
              <Pagination
                currentPage={clientPage}
                totalItems={videoData.length}
                totalItemsPerPage={pageSize}
                onPageChange={(page: number) => setClientPage(page)}
              />
            )}
          </div>

          {/* Right: page-size selector + range. Outer container aligns to flex-end
              so this row's bottom edge lines up with the pagination's bottom edge,
              regardless of the height difference between the two controls. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              aria-label="Responses per page"
              style={{
                minWidth: '140px',
                height: '38px',
                padding: '0.4rem 0.75rem',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                fontSize: '0.9rem',
                color: '#495057',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
            >
              {PAGE_SIZE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <span style={{ fontSize: '0.9rem', color: '#495057', whiteSpace: 'nowrap' }}>
              Showing {rangeStart}–{rangeEnd} of {videoData.length}
            </span>
          </div>
        </div>
      )}

      <ScrollToTopButton isMobile={isMobile} displayedCards={displayedCards} />

      {/* Expanded Interview Overlay */}
      {isExpandedInterviewOverlayFeatureEnabled() && (
        <ExpandedInterviewPageOverlay
          isOpen={overlayState.isOpen}
          onClose={handleCloseOverlay}
          selectedVideo={overlayState.selectedVideo}
          allVideos={overlayState.allVideos}
          filters={{
            selectedJob,
            selectedDateRange,
            appliedQuestions
          }}
          isMobile={isMobile}
        />
      )}

    </div>
  )
}

const JobSelectionFilter = React.memo(({ 
  isMobile, 
  jobData, 
  isLoadingJobs, 
  isLoadingAllJobs,
  selectedJob,
  setSelectedJob,
  filteredCount,
  shownCount,
  totalResponses,
  selectedDateRange,
  setSelectedDateRange,
  datePickerDefault,
  isQuestionCanvasOpen,
  setIsQuestionCanvasOpen,
  appliedQuestions,
  selectedQuestions,
  setSelectedQuestions,
  setAppliedQuestions,
  availableQuestions,
  getQuestionResponseCount,
  displayedCards,
  questionnaireTitle,
  onRestoreCompanyInterviews,
  jobsWithQuestionnaires,
  isLoadingJobQuestionnaires,
  onJobsDiscovered,
  paginationMeta,
  isFilterDataLoading
}: {
  isMobile: boolean,
  jobData: JobData[] | null,
  isLoadingJobs?: boolean,
  isLoadingAllJobs?: boolean,
  selectedJob: JobData | null,
  setSelectedJob: (job: JobData | null) => void,
  filteredCount: number,
  shownCount: number,
  totalResponses: number,
  selectedDateRange: string,
  setSelectedDateRange: (range: string) => void,
  datePickerDefault: string,
  isQuestionCanvasOpen: boolean,
  setIsQuestionCanvasOpen: (open: boolean) => void,
  appliedQuestions: number | null,
  selectedQuestions: number | null,
  setSelectedQuestions: (questions: number | null) => void,
  setAppliedQuestions: (questions: number | null) => void,
  availableQuestions: IQuestion[],
  getQuestionResponseCount: (questionId: number) => number,
  displayedCards: number,
  questionnaireTitle: string | null,
  onRestoreCompanyInterviews?: () => void,
  jobsWithQuestionnaires?: Set<number>,
  isLoadingJobQuestionnaires?: boolean,
  onJobsDiscovered?: (jobs: JobData[]) => void,
  paginationMeta?: { total: number; totalPages: number } | null,
  isFilterDataLoading?: boolean
}) => {
  
  return (
    // Container
    <div style={{
      marginBottom: '2rem'
    }}
    >
      {/* VI-664: platforms <job-bar> replaces the custom job selector when enabled.
          Both paths feed the same setSelectedJob callback, so all downstream
          interview/filter logic (below) is unchanged. */}
      {isPlatformsJobBarEnabled() ? (
        <PlatformsJobBar
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          onRestoreCompanyInterviews={onRestoreCompanyInterviews}
        />
      ) : (
        <SelectJobFilterContainer
          isMobile={isMobile}
          jobData={jobData}
          isLoadingJobs={isLoadingJobs}
          isLoadingAllJobs={isLoadingAllJobs}
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          onRestoreCompanyInterviews={onRestoreCompanyInterviews}
          jobsWithQuestionnaires={jobsWithQuestionnaires}
          isLoadingJobQuestionnaires={isLoadingJobQuestionnaires}
          onJobsDiscovered={onJobsDiscovered}
          paginationMeta={paginationMeta}
        />
      )}
      <FilterInterviewContainer
      isMobile={isMobile}
      selectedJob={selectedJob}
      filteredCount={filteredCount}
      shownCount={shownCount}
      totalResponses={totalResponses}
      selectedDateRange={selectedDateRange}
      setSelectedDateRange={setSelectedDateRange}
      datePickerDefault={datePickerDefault}
      isQuestionCanvasOpen={isQuestionCanvasOpen}
      setIsQuestionCanvasOpen={setIsQuestionCanvasOpen}
      appliedQuestions={appliedQuestions}
      selectedQuestions={selectedQuestions}
      setSelectedQuestions={setSelectedQuestions}
      setAppliedQuestions={setAppliedQuestions}
      availableQuestions={availableQuestions}
      getQuestionResponseCount={getQuestionResponseCount}
      displayedCards={displayedCards}
      questionnaireTitle={questionnaireTitle}
      isFilterDataLoading={isFilterDataLoading}
      />
    </div>
  )
})

const SelectJobFilterContainer = ( { 
  isMobile, 
  jobData, 
  isLoadingJobs, 
  isLoadingAllJobs,
  selectedJob, 
  setSelectedJob,
  showSelectedJob = true,
  showDropdown = true,
  onRestoreCompanyInterviews,
  jobsWithQuestionnaires,
  isLoadingJobQuestionnaires,
  onJobsDiscovered,
  paginationMeta
}: { 
  isMobile: boolean, 
  jobData?: JobData[] | null, 
  isLoadingJobs?: boolean,
  isLoadingAllJobs?: boolean,
  selectedJob: JobData | null, 
  setSelectedJob?: (job: JobData | null) => void,
  showSelectedJob?: boolean,
  showDropdown?: boolean,
  onRestoreCompanyInterviews?: () => void,
  jobsWithQuestionnaires?: Set<number>,
  isLoadingJobQuestionnaires?: boolean,
  onJobsDiscovered?: (jobs: JobData[]) => void,
  paginationMeta?: { total: number; totalPages: number } | null
} ) => {
  const evenSmallerDevices = window.innerWidth < 345; // smaller then normal mobile (i.e galaxy z fold 5)
  const smallerDesktops = !isMobile && window.innerWidth <= 1280
  // console.log("SELECTED JOB", selectedJob);
  const [isJobCanvasOpen, setIsJobCanvasOpen] = useState(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // SEARCH STATE
  const [searchFilters, setSearchFilters] = useState({
    keyword: '',
    divisionId: 'all',
    userId: 'all',
    status: 'all'
  });

  // PAGINATION STATE
  const [currentJobPage, setCurrentJobPage] = useState(0);
  const [paginatedJobs, setPaginatedJobs] = useState<JobData[]>([]);
  const [totalJobPages, setTotalJobPages] = useState(1);
  const [totalJobCount, setTotalJobCount] = useState(0);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const pageCache = useRef<Map<number, JobData[]>>(new Map());
  const JOBS_PER_PAGE = 50;

  // SEARCH API (reusable hook)
  const searchApi = useJobSearchApi(onJobsDiscovered);

  // Sync initial jobData into paginatedJobs and seed page 0 cache
  useEffect(() => {
    if (jobData && jobData.length > 0 && paginatedJobs.length === 0) {
      setPaginatedJobs(jobData);
      pageCache.current.set(0, jobData);
    }
  }, [jobData]);

  // Sync pagination metadata from parent (no extra API call needed)
  useEffect(() => {
    if (paginationMeta) {
      setTotalJobCount(paginationMeta.total);
      setTotalJobPages(paginationMeta.totalPages);
    }
  }, [paginationMeta]);

  const filteredJobData = useMemo(() => {
    let baseJobs: JobData[];

    if (searchApi.hasSearched) {
      const keyword = searchFilters.keyword.toLowerCase().trim();
      const seenIds = new Set<number>();
      const combined: JobData[] = [];

      for (const job of searchApi.searchResults) {
        if (!seenIds.has(job.id)) {
          seenIds.add(job.id);
          combined.push(job);
        }
      }

      // Also match locally across all known jobs (jobData includes all merged/discovered jobs)
      if (keyword) {
        for (const job of (jobData || [])) {
          if (!seenIds.has(job.id) && (
            job.title?.toLowerCase().includes(keyword) ||
            job.companyName?.toLowerCase().includes(keyword) ||
            job.requisitionName?.toLowerCase().includes(keyword)
          )) {
            seenIds.add(job.id);
            combined.push(job);
          }
        }
      }

      baseJobs = combined;
    } else {
      baseJobs = paginatedJobs.length > 0 ? paginatedJobs : (jobData || []);
    }

    let filtered = [...baseJobs];

    if (!searchApi.hasSearched && searchFilters.keyword.trim()) {
      const keyword = searchFilters.keyword.toLowerCase().trim();
      filtered = filtered.filter(job => 
        job.title?.toLowerCase().includes(keyword) ||
        job.companyName?.toLowerCase().includes(keyword) ||
        job.requisitionName?.toLowerCase().includes(keyword)
      );
    }
    
    if (searchFilters.status !== 'all') {
      filtered = filtered.filter(job => 
        job.status?.toLowerCase() === searchFilters.status.toLowerCase()
      );
    }
    
    if (searchFilters.divisionId !== 'all') {
      filtered = filtered.filter(job => 
        job.divisionId?.toString() === searchFilters.divisionId
      );
    }
    
    if (searchFilters.userId !== 'all') {
      filtered = filtered.filter(job => 
        job.recruiterId?.toString() === searchFilters.userId
      );
    }
    
    return filtered;
  }, [jobData, paginatedJobs, searchApi.searchResults, searchApi.hasSearched, searchFilters]);

  const cityAndState = selectedJob ? `${selectedJob.locations[0].city} , ${getStateAbbreviation(selectedJob.locations[0].region)}` : '';

  // PAGINATION HANDLER (with client-side cache)
  const handlePageChange = useCallback(async (page: number) => {
    setCurrentJobPage(page);

    const cached = pageCache.current.get(page);
    if (cached) {
      setPaginatedJobs(cached);
      return;
    }

    setIsLoadingPage(true);
    try {
      const result = await JobsService.getJobsPaginated(undefined, page, JOBS_PER_PAGE, true);
      pageCache.current.set(page, result.jobs);
      setPaginatedJobs(result.jobs);
      setTotalJobPages(result.totalPages);
      setTotalJobCount(result.total);
      onJobsDiscovered?.(result.jobs);
    } catch (error: unknown) {
      console.error('Error fetching jobs page:', error);
    } finally {
      setIsLoadingPage(false);
    }
  }, [onJobsDiscovered]);

  // SEARCH HANDLER - filters local + calls Job Manager API for keyword search
  const handleSearch = useCallback(async (keyword: string, divisionId: string, userId: string, status?: string) => {
    setSearchFilters({
      keyword: keyword || '',
      divisionId: divisionId || 'all',
      userId: userId || 'all',
      status: status || 'all'
    });

    if (keyword?.trim()) {
      searchApi.searchByKeyword(keyword);
    } else {
      searchApi.clearSearch();
    }
  }, [searchApi]);

  // CLEAR SEARCH — reset pagination back to page 1
  const handleClearSearch = useCallback(() => {
    setSearchFilters({
      keyword: '',
      divisionId: 'all',
      userId: 'all',
      status: 'all'
    });
    searchApi.clearSearch();

    setCurrentJobPage(0);
    const firstPage = pageCache.current.get(0);
    if (firstPage) {
      setPaginatedJobs(firstPage);
    } else if (jobData) {
      setPaginatedJobs(jobData);
    }
  }, [searchApi, jobData]);

  const handleJobClick = (jobId: number) => {
    const allJobs = [...(paginatedJobs.length > 0 ? paginatedJobs : (jobData || [])), ...searchApi.searchResults];
    const job = allJobs.find((j) => j.id === jobId) || null;
    setSelectedJob?.(job);
    setIsJobCanvasOpen(false);
  }

  const handleClearJob = () => {
    console.log('🧹 Clearing job filter and restoring company-wide interviews...');
    setSelectedJob?.(null);
    setIsDropdownOpen(false);
    
    // Restore company-wide interviews when clearing job filter
    if (onRestoreCompanyInterviews) {
      onRestoreCompanyInterviews();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
  
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
  
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div
        style={{
          width: '100%',
          height: isMobile ? 'auto' : '70px',
          // backgroundColor: 'blue',
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'left',
          border: '1px solid #e0e0e0',
          borderRadius: '10px',
          // marginBottom: isMobile ? '2.0rem' : '0'
          paddingBottom: isMobile ? '1rem' : '0',
        }}
      >
        {/* Container for the job selection filter texts */}
        <div
          style={{
            margin: isMobile ? '1.5rem auto auto 1rem' : 'auto auto auto 1rem',
            alignItems: isMobile ? 'flex-start' : 'center',
            width: '100%',
            height: '100%',
            // backgroundColor: 'green',
            display: 'flex',
            gap: isMobile ? '0.25rem' : '0.5rem',
            cursor: 'default',
            flexDirection: isMobile ? 'column' : 'row' // Stack on mobile
            
          }}
        >

          <div 
            style={{ 
              // backgroundColor: 'red',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile? '0.25rem' : '0.5rem',
              paddingRight: !showSelectedJob ? '0.5rem' : undefined,
              alignItems: isMobile ? 'flex-start' : 'center',
            }}
          >
            <h4 
              style={{
                color: selectedJob ? 'black' : '#b5b5c3',
                fontSize: evenSmallerDevices ? '0.8rem' : isMobile ? '1.1rem' : '1.1rem',
                fontWeight: 600,
                margin: 0,
                display: 'flex',           // Make it a flex container
                alignItems: 'center',      // Center items vertically
                gap: '0.25rem', 
                wordBreak: isMobile ? 'break-word' : 'normal',
                maxWidth: evenSmallerDevices ? '280px' : !showSelectedJob && !isMobile && smallerDesktops ?'70%' : 'none',
                width: isMobile ? undefined : 'fit-content',
                // backgroundColor: 'green',
              }}
            >
              {selectedJob && (
                <div 
                style={{ 
                  position: 'relative', 
                  display: 'inline-block',
                }}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <svg 
                  width={isMobile ? '1.2rem' : '1.2rem'}  
                  height={isMobile ? '1.2rem' : '1.2rem'} 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    color: '#1bc5bd',
                    // marginRight: '0.25rem',
                    // alignSelf: 'center'
                  }}
                >
                  <g clipPath="url(#clip0_2584_11412)">
                    <path d="M12 1.875C6.375 1.875 1.875 6.42188 1.875 12C1.875 17.625 6.375 22.125 12 22.125C17.5781 22.125 22.125 17.625 22.125 12C22.125 6.42188 17.5312 1.875 12 1.875ZM12 0.375C18.375 0.375 23.625 5.625 23.625 12C23.625 18.4219 18.375 23.625 12 23.625C5.57812 23.625 0.375 18.4219 0.375 12C0.375 5.625 5.57812 0.375 12 0.375ZM10.3125 16.5H10.875V10.875H10.3125C9.98438 10.875 9.75 10.6406 9.75 10.3125V9.9375C9.75 9.65625 9.98438 9.375 10.3125 9.375H12.5625C12.8438 9.375 13.125 9.65625 13.125 9.9375V16.5H13.6875C13.9688 16.5 14.25 16.7812 14.25 17.0625V17.4375C14.25 17.7656 13.9688 18 13.6875 18H10.3125C9.98438 18 9.75 17.7656 9.75 17.4375V17.0625C9.75 16.7812 9.98438 16.5 10.3125 16.5ZM12 5.25C12.7969 5.25 13.5 5.95312 13.5 6.75C13.5 7.59375 12.7969 8.25 12 8.25C11.1562 8.25 10.5 7.59375 10.5 6.75C10.5 5.95312 11.1562 5.25 12 5.25Z" fill="currentColor"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_2584_11412">
                      <rect width="24" height="24" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                  <JobTooltip 
                      job={selectedJob}
                      // position={(showSelectedJob && !isMobile) ? 'bottom' : 'right'}
                      position="right" // force right for all devices
                      show={showTooltip}
                  />
                 </div>
                
            )}
              {selectedJob ? (isMobile ? truncateName(selectedJob?.title, showSelectedJob ? 25 : 35) : selectedJob?.title) : 'No Job Selected'}
            </h4>
            {selectedJob && 
              <span 
                style={{ 
                  fontSize: isMobile ? '0.7rem' : '0.8rem', 
                  fontWeight: 400,
                  whiteSpace: 'nowrap',        // prevents line breaking
                  overflow: 'hidden',          // hides overflow
                  textOverflow: 'ellipsis',    // shows ... when text is cut off
                  maxWidth: evenSmallerDevices ? '120px' : isMobile ? '150px' : 'none' 
                }}>{cityAndState}
            </span>}
          </div>

          {/* Select Job */}
          {showSelectedJob && (
          <div 
            style={{ 
              display: 'flex', 
              // backgroundColor: 'pink', 
              marginTop: '0rem', 
              gap: '0.5rem', 
              fontWeight: '700',
              fontSize: isMobile ? '0.7rem' : '0.8rem',
              // alignItems: isMobile ? 'flex-start' : 'center'
            }}
          >
            {!isMobile && <span>|</span>}
            <span style={{ cursor: 'pointer', marginTop: '0' }} onClick={() => setIsJobCanvasOpen(true)}> {selectedJob ? 'Change' : 'Select'} Job </span>
          </div>
          )}

          <JobListOffCanvas
            isOpen={isJobCanvasOpen}
            onClose={() => setIsJobCanvasOpen(false)}
            title="Select Job"
            totalItems={totalJobCount || jobData?.length || 0}
            itemsPerPage={JOBS_PER_PAGE}
            showPagination={!searchApi.hasSearched && totalJobPages > 1}
            onPageChange={(page) => handlePageChange(page - 1)}
            controlledPage={currentJobPage + 1}
          >
            {/* <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              
            }}> */}
            <div>
              <div style={{
                backgroundColor: '#f8f9fa',
                // backgroundColor: 'red',
                width: 'calc(100% + ' + (isMobile ? '1.5rem' : '2rem') + ')',
                // height: isMobile ? 'auto' : '180px',
                // minHeight: isMobile ? '90px' : '180px',
                marginLeft: isMobile ? '-0.75rem' : '-1rem',
                marginTop: isMobile ? '-0.75rem' : '-1rem',
                marginBottom: isMobile ? '-0.75rem' : '-1rem',
                padding: isMobile ? '0.75rem' : '1rem',
                boxSizing: 'border-box',
              }}
              >
              <SearchSection 
                onSearch={handleSearch} 
                showPostedBy={false}
                showJobStatus={false}
                showDivision={false}
              />
              </div>

              {/* SEARCH RESULTS INFO */}
              {(searchFilters.keyword || searchFilters.status !== 'all' || searchFilters.divisionId !== 'all' || searchFilters.userId !== 'all') && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem',
                  marginTop: '1rem'
                }}>
                  <span>
                    {searchApi.isSearching ? 'Searching...' : searchApi.hasSearched
                      ? `${filteredJobData.length} ${filteredJobData.length === 1 ? 'job matches' : 'jobs match'} your search`
                      : `${filteredJobData.length} of ${totalJobCount || jobData?.length || 0} jobs match your search`
                    }
                  </span>
                  <button
                    onClick={handleClearSearch}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      marginLeft: 'auto',
                      fontSize: '1rem',
                      color: 'black',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: '500',
                    }}
                  >
                    <i className="fal fa-xmark"></i>
                  </button>
                </div>
              )}
              <div style={{
                marginTop: '2rem',
                // backgroundColor: 'red',
              }}
              >
                <InterviewJobListContent 
                    jobData={filteredJobData || null}
                    isLoading={isLoadingJobs || isLoadingPage || searchApi.isSearching}
                    isLoadingAllJobs={isLoadingAllJobs}
                    handleJobClick={handleJobClick}
                    selectedJob={selectedJob}
                    jobsWithQuestionnaires={jobsWithQuestionnaires}
                    isLoadingJobQuestionnaires={isLoadingJobQuestionnaires}
                />
              </div>
            </div>
          </JobListOffCanvas>
        </div>

        {/* Dropdown */}
        {showDropdown && selectedJob && !isMobile && (
          <div className="position-relative" ref={dropdownRef}>
            <div
              style={{
                display: 'flex',
                marginLeft: 'auto',
                marginRight: '1rem',
                height: '38px',
                width: '38px',
                backgroundColor: '#f8f9fa',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '16px'
              }}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <i className="fas fa-ellipsis-vertical"></i>
            </div>
          
            {/* DROPDOWN MENU */}
            {isDropdownOpen && (
              <div
                className="position-absolute bg-white border rounded shadow-sm"
                style={{
                  top: '100%',
                  right: '0', // Changed from '0' to '1rem' to align with button
                  marginTop: '4px',
                  minWidth: '150px',
                  zIndex: 1000,
                  border: '1px solid #dee2e6'
                }}
              >
                <div
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  onClick={handleClearJob}
                >
                  <img src="/svgs/times.svg" alt="Close" style={{ marginRight: '8px', fontSize: '8px' }}></img>
                  Clear Job
                </div>
              </div>
            )}
        </div>
        )}

        {/* Dropdown */}
        {showDropdown && selectedJob && isMobile && (
          <div className="position-relative" ref={dropdownRef}>
            <div
              style={{
                display: 'flex',
                marginLeft: 'auto',
                marginRight: '1rem',
                height: '38px',
                width: '38px',
                backgroundColor: '#f8f9fa',
                // backgroundColor: 'red',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '16px'
              }}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <i className="fas fa-ellipsis-vertical"></i>
            </div>
        
            {/* DROPDOWN MENU */}
            {isDropdownOpen && (
              <div
                className="position-absolute bg-white border rounded shadow-sm"
                style={{
                  // top: '100%',
                  right: '0',
                  marginTop: '4px',
                  minWidth: '150px',
                  zIndex: 1000,
                  border: '1px solid #dee2e6'
                }}
              >
                <div
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  onClick={handleClearJob}
                >
                  <img src="/svgs/times.svg" alt="Close" style={{ marginRight: '8px', fontSize: '8px' }}></img>
                  Clear Job
                </div>
              </div>
            )}
          </div>
        )}
        
    </div>
  )
}

const DisplayFilterSection: React.FC<{
  appliedQuestion: number | null;
  isMobile: boolean;
  availableQuestions: IQuestion[];
  setAppliedQuestion: (question: number | null) => void;
  setSelectedQuestions: (questions: number | null) => void;
  setAppliedQuestionnaireId: (id: string | null) => void;
  setSelectedQuestionnaireId: (id: string | null) => void;
}> = ({
  appliedQuestion,
  isMobile,
  availableQuestions,
  setAppliedQuestion,
  setSelectedQuestions,
  setAppliedQuestionnaireId,
  setSelectedQuestionnaireId,
}) => {
  // Don't show anything if no question is applied
  if (appliedQuestion === null || appliedQuestion === undefined) return null;
  
  // Find the question text from the ID
  const questionObj = availableQuestions.find(q => q.id === appliedQuestion);
  const questionText = questionObj?.text || `Question ID: ${appliedQuestion}`;
  
  return (
    <div style={{ 
      marginBottom: '1rem', 
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? '1rem' : undefined
    }}
    >
      <span style={{ fontWeight: '600', fontSize: isMobile ? '0.875rem' : '1rem', cursor: 'default' }}>Filtered by:</span>
      <span style={{ 
          backgroundColor: '#e9ecef', 
          padding: '0.25rem 0.75rem', 
          borderRadius: '4px',
          fontSize: isMobile ? '0.75rem' : '0.875rem',
          color: '#6C757D',
          marginLeft: '0.5rem',
        }}>
          <b>Question:</b> {questionText}
          <Tooltip message="Clear Filter" position="top">
            <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg" 
            style={{
              marginLeft: '0.5rem',
              cursor: 'pointer',
            }}
            onClick={() => {
              setAppliedQuestion(null);
              setSelectedQuestions(null);
              setAppliedQuestionnaireId(null);
              setSelectedQuestionnaireId(null);
            }}
            >
              <path d="M8 1.58203C5.94922 1.58203 4.08984 2.67578 3.05078 4.42578C2.03906 6.20312 2.03906 8.36328 3.05078 10.1133C4.08984 11.8906 5.94922 12.957 8 12.957C10.0234 12.957 11.8828 11.8906 12.9219 10.1133C13.9336 8.36328 13.9336 6.20312 12.9219 4.42578C11.8828 2.67578 10.0234 1.58203 8 1.58203ZM8 14.2695C5.48438 14.2695 3.1875 12.957 1.92969 10.7695C0.671875 8.60938 0.671875 5.95703 1.92969 3.76953C3.1875 1.60938 5.48438 0.269531 8 0.269531C10.4883 0.269531 12.7852 1.60938 14.043 3.76953C15.3008 5.95703 15.3008 8.60938 14.043 10.7695C12.7852 12.957 10.4883 14.2695 8 14.2695ZM5.78516 5.05469C6.03125 4.80859 6.44141 4.80859 6.6875 5.05469L7.97266 6.33984L9.25781 5.05469C9.53125 4.80859 9.94141 4.80859 10.1875 5.05469C10.4609 5.32812 10.4609 5.73828 10.1875 5.98438L8.90234 7.26953L10.1875 8.55469C10.4609 8.82812 10.4609 9.23828 10.1875 9.48438C9.94141 9.75781 9.53125 9.75781 9.25781 9.48438L7.97266 8.19922L6.6875 9.48438C6.44141 9.75781 6.03125 9.75781 5.78516 9.48438C5.51172 9.23828 5.51172 8.82812 5.78516 8.55469L7.07031 7.26953L5.78516 5.98438C5.51172 5.73828 5.51172 5.32812 5.78516 5.05469Z" fill="#6C757D"/>
            </svg>
          </Tooltip>

      </span>
      
    </div>
  );
};

const FilterInterviewContainer = React.memo(({
  isMobile,
  selectedJob,
  filteredCount,
  shownCount,
  totalResponses,
  selectedDateRange,
  setSelectedDateRange,
  datePickerDefault,
  isQuestionCanvasOpen,
  setIsQuestionCanvasOpen,
  appliedQuestions,
  selectedQuestions,
  setSelectedQuestions,
  setAppliedQuestions,
  availableQuestions,
  getQuestionResponseCount,
  displayedCards,
  questionnaireTitle,
  isFilterDataLoading
}: {
  isMobile: boolean,
  selectedJob: JobData | null,
  filteredCount: number,
  shownCount: number,
  totalResponses: number,
  selectedDateRange: string,
  setSelectedDateRange: (range: string) => void,
  datePickerDefault: string,
  isQuestionCanvasOpen: boolean,
  setIsQuestionCanvasOpen: (open: boolean) => void,
  appliedQuestions: number | null,
  selectedQuestions: number | null,
  setSelectedQuestions: (questions: number | null) => void,
  setAppliedQuestions: (questions: number | null) => void,
  availableQuestions: IQuestion[],
  getQuestionResponseCount: (questionId: number) => number,
  displayedCards: number,
  questionnaireTitle: string | null,
  isFilterDataLoading?: boolean
}) => {
  const [isFilterButtonHovered, setIsFilterButtonHovered] = useState(false);

  return (
    <div
        style={{
          width: '100%',
          height: isMobile ? 'auto' : '70px', // Auto height on mobile
          minHeight: '70px', // Ensure minimum height
          display: 'flex',
          justifyContent: 'left',
          backgroundColor: '#f8f9fa',
          borderRadius: '10px',
          marginTop: '2rem',
          padding: isMobile ? '1rem' : '0', // Add padding on mobile
        }}
      >
        {/* Container for the job selection filter texts */}
        <div
          style={{
            margin: isMobile ? '0' : 'auto auto auto 1rem',
            width: '100%',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row', // Stack vertically on mobile
            gap: isMobile ? '1rem' : '0.5rem',
            alignItems: isMobile ? 'stretch' : 'center',
            cursor: 'default'
          }}
        >
          {/* For Interviews and Showing X submissions */}
          <div 
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem', 
              flex: isMobile ? 'none' : '1', // Don't flex on mobile
            }}
          >
            <span 
              style={{ 
              display: 'flex', 
              fontWeight: '600',
              fontSize: '1rem' 
            }}
            > Video Screeners </span>

            <span>
              {(() => {
                // When a question filter is active the total is the per-question
                // response count (a sub-filter the counts endpoint doesn't model);
                // otherwise use the authoritative completed total for the scope.
                const totalCount = appliedQuestions !== null
                  ? getQuestionResponseCount(appliedQuestions || 0)
                  : (totalResponses || 0);

                return `Showing ${shownCount || 0} of ${totalCount} responses`;
              })()}
            </span>
          </div>

          {/* For DatePicker and FilterButton */}
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row', // Stack vertically on mobile
              gap: '1rem', 
              marginLeft: isMobile ? '0' : 'auto', // No auto margin on mobile
              paddingRight: isMobile ? '0' : '1rem',
              marginTop: isMobile ? '0' : 'auto',
              width: isMobile ? '100%' : 'auto', // Full width on mobile
            }}
          >
              <DatePicker 
                name="date" 
                value={selectedDateRange} 
                onChangeHandler={setSelectedDateRange}
                id="date" 
                defaultRange={datePickerDefault}
                style={{ width: isMobile ? '100%' : 'auto' }}
              />

            {/* Wrapper owns the hover state so it survives the loading→
                active button swap. mouseenter only fires on cursor entry,
                not on mount, so without a stable wrapper the freshly
                mounted active button would render with isFilterButtonHovered
                === false even when the cursor was already over it. */}
            <div
              style={{ width: isMobile ? '100%' : 'auto' }}
              onMouseEnter={() => setIsFilterButtonHovered(true)}
              onMouseLeave={() => setIsFilterButtonHovered(false)}
            >
            {!selectedJob ? (
              <Tooltip message="Select a Job to Enable Filter" position="top">
                <Button
                  text={
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <img src="/svgs/filter.svg" alt="Filter" width="12" height="12" />
                      Filter by Questions
                    </span>
                  }
                  variant="outline-secondary"
                  onClick={() => {}}
                  disabled={!selectedJob}
                  style={{ width: isMobile ? '100%' : 'auto', cursor: 'not-allowed' }}
                />
              </Tooltip>
            ) : isFilterDataLoading ? (
              // While the per-job interviews and questionnaire history are
              // still in flight, the count we use to pick simple-vs-multi
              // drawer is unstable. Disable the button so the user can't
              // open a drawer that flips mid-render.
              <Tooltip message="Loading interview data…" position="top">
                <Button
                  text={
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Spinner size="sm" />
                      Filter by Questions
                    </span>
                  }
                  variant="outline-secondary"
                  onClick={() => {}}
                  disabled
                  style={{ width: isMobile ? '100%' : 'auto', cursor: 'not-allowed' }}
                />
              </Tooltip>
            ) : (
              <Button
                text={
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between', // This will push the badge to the right
                    width: '100%', // Ensure full width to allow space-between to work
                    color: isFilterButtonHovered ? 'white' : '#4291e0',
                    transition: 'color 0.2s ease',
                    gap: '0.5rem'
                  }}>
                    {/* Left side: Filter icon and text grouped together */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <svg width="12" height="10" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 1.17627C0 0.820801 0.273438 0.52002 0.65625 0.52002H11.5938C11.9492 0.52002 12.25 0.820801 12.25 1.17627C12.25 1.55908 11.9492 1.83252 11.5938 1.83252H0.65625C0.273438 1.83252 0 1.55908 0 1.17627ZM1.75 5.55127C1.75 5.1958 2.02344 4.89502 2.40625 4.89502H9.84375C10.1992 4.89502 10.5 5.1958 10.5 5.55127C10.5 5.93408 10.1992 6.20752 9.84375 6.20752H2.40625C2.02344 6.20752 1.75 5.93408 1.75 5.55127ZM7.21875 10.5825H5.03125C4.64844 10.5825 4.375 10.3091 4.375 9.92627C4.375 9.5708 4.64844 9.27002 5.03125 9.27002H7.21875C7.57422 9.27002 7.875 9.5708 7.875 9.92627C7.875 10.3091 7.57422 10.5825 7.21875 10.5825Z" fill="currentColor"/>
                      </svg>
                      Filter by Questions
                    </div>
                    
                    {/* Right side: Badge shows the 1-based position of the
                        selected question within availableQuestions. Pill-
                        shapes for 10+ so double digits don't overflow. */}
                    {selectedQuestions !== null && (() => {
                      const questionNumber =
                        availableQuestions.findIndex(q => q.id === selectedQuestions) + 1;
                      if (questionNumber === 0) return null;
                      return (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '16px',
                            height: '16px',
                            padding: questionNumber > 9 ? '0 4px' : 0,
                            borderRadius: '8px',
                            backgroundColor: isFilterButtonHovered ? 'white' : '#2F86DD',
                            color: isFilterButtonHovered ? '#2F86DD' : 'white',
                            fontSize: '10px',
                            fontWeight: 400,
                            lineHeight: 1,
                            transition: 'background-color 0.2s ease, color 0.2s ease',
                          }}
                        >
                          {questionNumber}
                        </span>
                      );
                    })()}
                  </span>
                }
                variant="outline-primary"
                onClick={() => {setIsQuestionCanvasOpen(true)}}
                disabled={!selectedJob}
                style={{ width: isMobile ? '100%' : 'auto' }}
              />
            )}
            </div>
          </div>
        </div>

        {/* QuestionCanvas is rendered at the top of InterviewsPageContent
            to keep state and history-fetching co-located with the page-level
            owner; the trigger button above just flips isQuestionCanvasOpen. */}
    </div>
  )
})

export default InterviewsPage